<template>
  <bid-open-center
    :project-id="projectId"
    :project-info="projectInfo"
    :show-footer="showActions"
    :left-time-config="decryptConfig"
    :bid-file-type="bidFileType"
    :theme="theme"
  >
    <template v-slot:body>
      <div
        class="decode-step"
        :class="{ 'decode-step-light': theme === 'light' }"
      >
        <div class="operation">
          <tab-switch
            :tab-list="tabList"
            :active-color="theme === 'dark' ? '#27D8FE' : undefined"
            :usually-color="theme === 'light' ? '#333' : undefined"
            font-size="24px"
            @tabChange="changeTab"
          >
            <template v-slot:content="{ tab }">
              <span>{{ tab.label }}({{ tab.num }})</span>
            </template>
          </tab-switch>
          <div class="operation-right">
            <el-select
              v-model="sortMethod"
              placeholder="请选择"
              size="medium"
              @change="getPageData"
            >
              <el-option
                v-for="item in sortByTimeDict.array"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
        </div>

        <el-table
          :data="list"
          class="m-t-24 hall-table"
          size="medium"
          :row-style="{ fontSize: '22px', textAlign: 'center' }"
          :header-cell-style="{ fontSize: '22px', textAlign: 'center' }"
          align="center"
        >
          <el-table-column prop="supplierName" label="投标人">
            <template slot-scope="{ row }">
              <div class="row-content">{{ row.supplierName || '-' }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="bidFileStatus" label="文件解密状态">
            <template slot-scope="{ row }">
              <div class="row-content">
                <status-tag
                  v-if="
                    quoteFileStatusDict.translate(row.offerBidFileStatus).label
                  "
                  :tag-style="{ display: 'inline' }"
                  :type="
                    quoteFileStatusDict.translate(row.offerBidFileStatus).type
                  "
                  :label="
                    quoteFileStatusDict.translate(row.offerBidFileStatus).label
                  "
                  :label-style="{
                    color: theme === 'dark' ? '#FEFFFF' : '',
                    fontSize: '22px'
                  }"
                />
                <span v-else>-</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="submitTime" label="文件提交时间">
            <template v-slot="{ row }">
              <div class="row-content">{{ row.submitTime || '-' }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="submitStatus" label="文件提交状态">
            <template v-slot="{ row }">
              <div class="row-content">
                <template v-if="isNotLatestFun(row)">
                  <span class="text-danger text-bold">非最新文件</span>
                  <el-tooltip
                    class="m-l-6"
                    placement="bottom"
                    width="200"
                    effect="dark"
                    content="该投标文件未使用最新答疑澄清文件制作，在开标大厅不支持解密"
                  >
                    <i class="el-icon-question dispel-doubts"></i>
                  </el-tooltip>
                </template>
                <span v-else>
                  {{
                    fileSubmitStatusDict.translate(row.submitStatus).label ||
                      '-'
                  }}
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="操作" align="center">
            <template v-slot="{ row }">
              <template v-if="showTableOperate(row)">
                <div v-if="whetherDecoded(row)">
                  <div>解密时间</div>
                  <div>{{ row.decryptTime }}</div>
                </div>
                <el-button
                  v-else-if="whetherReturn(row)"
                  type="text"
                  class="row-button"
                  @click="rejectQuoteFile(row, true)"
                >
                  查看退回详情
                </el-button>

                <template v-else>
                  <el-button
                    v-if="canDecrypt"
                    type="text"
                    class="row-button"
                    :disabled="isNotLatestFun(row)"
                    @click="decodeBidFile(row)"
                  >
                    解密
                  </el-button>
                  <el-button
                    type="text"
                    class="row-button"
                    @click="rejectQuoteFile(row, false)"
                  >
                    退回文件
                  </el-button>
                </template>
              </template>
            </template>
          </el-table-column>
        </el-table>

        <pagination
          v-if="total > 0"
          :total="total"
          :page.sync="page"
          :limit.sync="size"
          :page-sizes="[5, 10]"
          @pagination="getPageData"
        />

        <decrypt-flow
          ref="decryptFlow"
          :encrypt-file-id="currentBidder.encryptedBidFileId || 0"
          :bidder-id="currentBidder.bidderId || 0"
          :project-id="projectId"
          :review-model="reviewModel"
          @getResult="getDecryptResult"
        />
      </div>
    </template>

    <template v-slot:foot>
      <div class="flex row-right" style="height: 100%">
        <el-button v-if="canDecrypt" type="primary" @click="openMark">
          开标一览表
        </el-button>
        <el-button v-else type="primary" @click="startDecrypt">
          开启文件解密
        </el-button>
      </div>
    </template>
  </bid-open-center>
</template>

<script>
import BidOpenCenter from '@/components/kpb/open/BidOpenCenter.vue'
import TabSwitch from '@/components/common/TabSwitch.vue'
import Pagination from '@/components/common/Pagination.vue'
import DecryptFlow from '@/components/common/DecryptFlow.vue'
import StatusTag from '@/components/common/StatusTag.vue'
import QuoteFileReturnDialog from '@/components/kpb/open/QuoteFileReturnDialog.vue'
import {
  fileDecryptionStatusDict,
  fileSubmitStatusDict,
  quoteFileStatusDict,
  quoteOpenStatusDict,
  sortByTimeDict
} from '@/dictionary/hall/open-bid-hall'

import { asyncInterval } from '@/utils/utils'

import { openDialog } from '@/utils/open-dialog'
import {
  bidFileTypeDict,
  reviewModelDict
} from '@/dictionary/project/kpb-project'
import useFileDecrypt from '@/components/kpb/hooks/useFileDecrypt'
import { workbenchServiceDict } from '@/dictionary/system'
import DecryptTimeLimit from '@/components/kpb/open/DecryptTimeLimit.vue'
import { startQuoteOpenMarkApi } from '@/api/hall/quote-open'

let loopPageDataControl = null

/**
 * 报价开标解密
 */
export default {
  name: 'QuoteOpenDecode',

  components: { Pagination, StatusTag, DecryptFlow, TabSwitch, BidOpenCenter },

  props: {
    projectId: {
      type: [String, Number],
      default: 0
    },

    project: {
      type: Object,
      default: () => {}
    },

    reviewModel: {
      type: Number,
      default: reviewModelDict.DEFAULT.value
    },

    decryptConfig: {
      type: Object,
      default: () => {}
    },

    theme: {
      validator: function(value) {
        return ['dark', 'light'].indexOf(value) !== -1
      },
      default: 'dark'
    }
  },

  data() {
    return {
      quoteFileStatusDict,
      sortByTimeDict,
      fileSubmitStatusDict,

      tabList: [
        { label: '全部', value: '', num: 0 },
        { label: '未解密', value: quoteFileStatusDict.PENDING.value, num: 0 }
      ],
      tabIndex: 0,
      // 排序方式
      sortMethod: sortByTimeDict.SUBMIT_TIME.value,

      statisticsCount: {
        allCount: 0,
        waitCount: 0
      },
      list: [],
      page: 1,
      size: 10,
      total: 0,

      currentBidder: {},

      // 能否开始解密
      canDecrypt: false
    }
  },

  computed: {
    currentTab() {
      return this.tabList[this.tabIndex]
    },

    projectInfo() {
      return this.project.projectInfo || {}
    },

    showActions() {
      // 报价文件开标状态为等待开标或开标中时显示
      const { PENDING, IN } = quoteOpenStatusDict
      const showList = [PENDING, IN].map(item => item.value)
      return showList.includes(this.project.offerBidOpenStatus)
    },

    bidFileType() {
      return bidFileTypeDict.QUOTATION.value
    }
  },

  watch: {
    project: {
      handler(val) {
        if (val?.projectId) {
          // 需要增加解密时间限制
          if (val?.offerBidOpenStatus === quoteOpenStatusDict.PENDING.value) {
            this.canDecrypt = false
          } else {
            this.canDecrypt = true
          }
          if (this.canDecrypt) {
            this.loopPageData()
          } else {
            this.getPageData()
          }
        }
      },
      deep: true,
      immediate: true
    }
  },

  beforeDestroy() {
    loopPageDataControl?.stop()
  },

  methods: {
    loopPageData() {
      loopPageDataControl?.stop()
      loopPageDataControl = asyncInterval(this.getPageData, 2000)
      loopPageDataControl.start().then(() => {
        // 当没未解密的供应商时，停止轮询
        if (this.statisticsCount.waitCount === 0) {
          loopPageDataControl.stop()
        }
      })
    },

    getPageData() {
      const quoteFileService = useFileDecrypt(
        workbenchServiceDict.PURCHASER.alias,
        this.projectId,
        bidFileTypeDict.QUOTATION.value
      )
      const pageParams = {
        page: this.page,
        size: this.size,
        bidFileStatus: this.currentTab.value,
        sortBy: this.sortMethod
      }
      const requestList = [
        quoteFileService.getQuoteEncryptFilePage(pageParams),
        quoteFileService.getQuoteEncryptFileStatistics()
      ]
      return Promise.allSettled(requestList)
        .then(resList => {
          const pageRes = resList[0] || {}
          if (pageRes.status === 'fulfilled') {
            this.list = pageRes.value?.records || []
            this.total = pageRes.value?.total || 0
          }
          const statisticsRes = resList[1] || {}
          if (statisticsRes.status === 'fulfilled') {
            this.tabList[0].num = statisticsRes.value?.allCount || 0
            this.tabList[1].num = statisticsRes.value?.waitCount || 0
            this.statisticsCount = statisticsRes.value || {}
          }
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    /**
     * 切换解密状态进行筛选
     */
    changeTab(index) {
      this.tabIndex = index
      this.page = 1
      this.getPageData()
    },

    /**
     * 判断是否显示操作栏
     */
    showTableOperate(row) {
      return row.submitStatus === fileSubmitStatusDict.SUBMIT.value
    },

    /**
     * 判断当前文件是否已解密
     */
    whetherDecoded(row) {
      return row.offerBidFileStatus === quoteFileStatusDict.DECRYPTED.value
    },

    /**
     * 判断当前文件是否退回
     */
    whetherReturn(row) {
      return row.offerBidFileStatus === quoteFileStatusDict.RETURNED.value
    },

    /**
     * 退回报价文件
     */
    rejectQuoteFile(row, readOnly = false) {
      openDialog({
        component: QuoteFileReturnDialog,
        propsData: {
          projectId: this.projectId,
          bidder: row,
          readOnly: readOnly
        },
        eventObj: {
          closeDialog: () => this.getPageData()
        }
      })
    },

    /**
     * 文件解密
     */
    decodeBidFile(row) {
      this.currentBidder = row
      this.$nextTick(() => {
        this.$refs.decryptFlow
          .checkDecryptResult({
            showToast: false,
            bidFileType: bidFileTypeDict.QUOTATION.value
          })
          .then(res => {
            if (res.data?.status !== fileDecryptionStatusDict.SUCCESS.value) {
              this.$refs.decryptFlow.caTypeDialogVisible = true
            }
          })
      })
    },

    /**
     * 查询业务端解密结果
     */
    getDecryptResult(decryptStatus) {
      if (decryptStatus === fileDecryptionStatusDict.FAIL.value) {
        this.$message.error('文件解密失败，请重新解密')
        this.getPageData()
      }
      if (decryptStatus === fileDecryptionStatusDict.SUCCESS.value) {
        this.$message.success('文件解密成功')
        this.getPageData()
      }
    },

    /**
     * 打开开标一览表
     */
    openMark() {
      this.$antConfirm({
        title: '确定开启开标一览表吗？',
        okText: '确定',
        cancelText: '取消',
        icon: 'exclamation-circle',
        centered: true,
        onOk: () => {
          startQuoteOpenMarkApi(this.projectId)
            .then(res => {
              this.$emit('nextStep')
              this.$store.dispatch('reviewHall/changeAudioShow', true)
              this.$store.dispatch('reviewHall/changeChantPlay', true)
              this.$store.dispatch('reviewHall/changeChantPlayContent', {
                projectId: this.projectId,
                bidFileType: this.bidFileType
              })
            })
            .catch(err => {
              if (err?.message) this.$message.error(err.message)
            })
        }
      })
    },

    /**
     * 开启报价文件解密
     */
    startDecrypt() {
      openDialog({
        component: DecryptTimeLimit,
        propsData: {
          projectId: this.projectId,
          bidFileType: bidFileTypeDict.QUOTATION.value
        },
        eventObj: {
          afterStart: () => this.$emit('reload')
        }
      })
    },

    /**
     * 判断是否是：非最新文件（澄清前上一次已提交，但澄清后未提交最新的投标文件）
     * 非最新文件 不支持解密
     */
    isNotLatestFun(row) {
      return (
        row.latestBidSubmitStatus === fileSubmitStatusDict.INVALID.value &&
        row.submitStatus === fileSubmitStatusDict.SUBMIT.value
      )
    }
  }
}
</script>

<style scoped lang="scss">
.center {
  width: 100%;
  flex: 1;
  margin: 0 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;

  // 浅色主体的颜色修改
  &.center-light {
    .center-main {
      .center-main-title {
        color: $text-color;

        .play-box {
          color: $primary-color;
        }
      }

      ::v-deep .el-table {
        tr,
        th,
        td {
          color: $text-color;
        }
        th {
          background-color: rgba(24, 144, 255, 0.12);
        }
      }
    }
  }

  .center-main {
    position: relative;
    width: 100%;
    flex: 1;
    flex-shrink: 0;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid rgba(24, 144, 255, 0.4);

    &:before {
      content: '';
      position: absolute;
      bottom: -2px;
      left: -2px;
      width: 28px;
      height: 28px;
      border: 4px solid #00a2ff;
      border-top-color: transparent;
      border-right-color: transparent;
    }
    &:after {
      content: '';
      position: absolute;
      bottom: -2px;
      right: -2px;
      width: 28px;
      height: 28px;
      border: 4px solid #00a2ff;
      border-top-color: transparent;
      border-left-color: transparent;
    }

    .center-main-title {
      width: 100%;
      height: 56px;
      font-size: 24px;
      @include text-bold;
      color: #fdffff;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 12px;
      overflow: hidden;
      background-color: rgba(24, 144, 255, 0.12);

      .title-box {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .play-box {
        width: 200px;
        display: flex;
        color: #27d8fe;
        justify-content: flex-end;
        .play-btn {
          font-size: 16px;
          margin-left: 20px;
          cursor: pointer;
          display: flex;
          align-items: center;
          margin-top: 2px;
          .icon {
            font-size: 24px;
            margin-right: 6px;
            margin-top: -2px;
          }
        }
      }
      span {
        width: 100%;
        display: inline-block;
      }

      .center-main-title-content {
        width: 100%;
      }
    }

    .center-main-content {
      width: 100%;
      margin-top: 43px;
      flex: 1;
      overflow: hidden;
    }
  }

  ::v-deep .el-table {
    background: transparent;
    color: #feffff;
    border: none;

    &::before,
    .el-table--group::after,
    .el-table--border::after {
      background-color: #d7cdd6;
    }

    &::before {
      height: 0px;
    }

    th {
      background: #072448;
      color: #feffff;
      border: none;
    }

    tr {
      background: transparent;
      color: #feffff;
    }

    .el-table__body {
      tr:hover > td {
        background: transparent;
      }
    }

    th.is-leaf,
    td {
      border-bottom: 1px solid rgba(215, 205, 214, 0.1);
    }

    .el-table__empty-text {
      font-size: 20px;
      line-height: 80px;
    }
  }

  // 深色模式下修改分页组件样式
  &:not(.center-light) {
    ::v-deep .pagination-container {
      background: transparent;

      .el-pagination__total {
        color: #feffff;
      }

      .el-input__inner {
        background: transparent;
        color: #feffff;
      }

      .el-pagination.is-background .btn-prev,
      .el-pagination.is-background .btn-next,
      .el-pagination.is-background .el-pager li {
        background: transparent;
        color: #feffff;
      }

      .el-pagination.is-background .el-pager li:not(.disabled).active {
        color: #27d8fe;
        border-color: #27d8fe;
      }

      .el-pagination__jump {
        color: #feffff;
      }
    }
  }
}

.decode-step {
  // 浅色主体下的样式修改
  &.decode-step-light {
    .hall-table {
      ::v-deep .el-button {
        color: $primary-color !important;

        &.is-disabled {
          color: #C0C4CC !important;
        }
      }

      ::v-deep .el-table__empty-text {
        font-size: 20px;
        line-height: 80px;
      }
    }
  }

  // 表格上方 tab切换 搜索的样式
  .operation {
    margin-left: 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &:not(.decode-step-light) {
    .operation-right {
      ::v-deep .el-select {
        .el-input__inner {
          background: transparent;
          color: $white-color;
        }
      }
    }
  }

  // 表格
  .hall-table {
    .row-content {
      font-size: 22px;
      text-align: center;
    }

    ::v-deep .el-button {
      color: #27d8fe !important;

      &.is-disabled {
        opacity: 0.6;
      }
    }
  }
}
</style>
